/**
 * The market at Lanternfall: three faction counters, a daily rotation, and what standing is
 * worth at each of them.
 *
 * Data only. The server owns every Wick it moves; this module is the single table both sides
 * read, so the shelf a player is looking at is the shelf the server bills from. Nothing here
 * has a side effect and nothing here reads a clock — the day index is always passed in.
 */

import { ITEMS, RARITY_NAMES, type RarityLevel } from './items.js';
import { FACTION_NAMES, FACTION_TIERS, type FactionId } from './progression.js';

// ---------------------------------------------------------------- standing

/**
 * What a faction calls you, by `factionTier` (0..4). The Compact keeps a ledger, the Hymnal
 * keeps a roll, the Ferry keeps a memory; they all use the same five words for it.
 */
export const STANDING_NAMES = ['Stranger', 'Known', 'Counted', 'Kept', 'Named'] as const;

/**
 * The discount a counter gives at each tier, as a fraction of the list price. Stranger pays
 * the sign; Named pays 15% under it. The curve is front-loaded — the first tier is the one
 * that has to feel like it bought something — and it applies to bought stock and to the
 * Compact's insurance premiums, never to what a counter pays you for your own gear.
 */
export const STANDING_DISCOUNTS = [0, 0.05, 0.09, 0.12, 0.15] as const;

const clampTier = (tier: number): number =>
  Math.max(0, Math.min(FACTION_TIERS.length - 1, Math.floor(tier)));

/** Discount fraction (0..0.15) for a faction tier. Out-of-range tiers clamp, never throw. */
export function standingDiscount(factionTier: number): number {
  return STANDING_DISCOUNTS[clampTier(factionTier)] ?? 0;
}

/** The word a counter uses for you at this tier. */
export function standingName(factionTier: number): string {
  return STANDING_NAMES[clampTier(factionTier)] ?? 'Stranger';
}

/** Apply a standing discount to a list price. Never below 1 Wick — nothing here is free. */
export function discounted(price: number, factionTier: number): number {
  return Math.max(1, Math.round(price * (1 - standingDiscount(factionTier))));
}

// ---------------------------------------------------------------- counters

export interface CounterDef {
  id: FactionId;
  /** `FACTION_NAMES[id]`, denormalised so a UI can draw a counter without a second lookup. */
  name: string;
  /** What is painted over the counter. */
  sign: string;
  blurb: string;
  /** Markup over `ItemDef.value` before any standing discount. */
  markup: number;
  /** Always on the shelf. */
  staples: readonly string[];
  /** Pool the daily rotation draws from. */
  rotating: readonly string[];
  /** How many rotating lines are drawn each day. */
  rotatingCount: number;
  /** True for the counter that writes insurance warrants (the Compact, and only the Compact). */
  insurance: boolean;
}

/**
 * Three counters, three jobs. The Compact keeps you alive and will not sell you a weapon that
 * matters; the Hymnal sells the Choir back to you a piece at a time; the Ferry sells whatever
 * came off the Band last night and will not have it tomorrow.
 */
export const COUNTERS: readonly CounterDef[] = [
  {
    id: 'compact',
    name: FACTION_NAMES.compact,
    sign: 'Issue & Warrant',
    blurb: 'Kit, cloth and insurance. The Compact would rather you came back than won.',
    markup: 1.15,
    staples: ['med_stim', 'med_kit', 'arm_waker_chest_common'],
    rotating: [
      'wpn_compact_blade',
      'wpn_long_vigil',
      'arm_waker_head_common',
      'arm_waker_shoulders_common',
      'arm_waker_hands_common',
      'arm_waker_legs_common',
      'arm_waker_feet_common',
      'wire_bundle',
      'scrap_hull',
    ],
    rotatingCount: 3,
    insurance: true,
  },
  {
    id: 'hymnal',
    name: FACTION_NAMES.hymnal,
    sign: 'Salvage of the Song',
    blurb:
      'Hymn-tech, cut down to something a hand can hold. They will not say where it came from.',
    markup: 1.5,
    staples: ['ember_glass'],
    rotating: [
      'wpn_ashfall_censer',
      'arm_vigil_head_uncommon',
      'arm_vigil_shoulders_uncommon',
      'arm_vigil_chest_uncommon',
      'arm_vigil_hands_uncommon',
      'hymn_shard',
      'data_slate',
    ],
    rotatingCount: 2,
    insurance: false,
  },
  {
    id: 'ferry',
    name: FACTION_NAMES.ferry,
    sign: 'Nightward Stock',
    blurb: 'Whatever came off the Downs last night. It will not be here tomorrow and they know it.',
    markup: 1.9,
    staples: [],
    rotating: [
      'wpn_tithe_cleaver',
      'arm_bulwark_head_uncommon',
      'arm_bulwark_shoulders_uncommon',
      'arm_bulwark_chest_uncommon',
      'arm_bulwark_legs_uncommon',
      'candle_relic',
      'choir_core',
      'colonist_tag',
    ],
    rotatingCount: 3,
    insurance: false,
  },
];

/**
 * Sabel Roque keeps the Compact's counter, and in Lanternfall she keeps all three: hers is the
 * only body in the city that buys, sells and repairs, so the in-world panel opens on her shelf and
 * offers the other two as tabs rather than pretending there are three people behind the stall.
 *
 * `SABEL_NPC_ID` is her `NpcDef.id` in `maps/lanternfall.ts`; the server checks it before it will
 * take Wick for a repair, so "repair" is a thing you do at a counter and not a thing you do in a
 * field with the panel open.
 */
export const SABEL_NPC_ID = 2;
export const SABEL_COUNTER_ID: FactionId = 'compact';
export const SABEL_NAME = 'Sabel Roque';

export const COUNTER_BY_ID: Record<FactionId, CounterDef> = Object.fromEntries(
  COUNTERS.map((c) => [c.id, c]),
) as Record<FactionId, CounterDef>;

export const isCounterId = (s: string): s is FactionId =>
  Object.prototype.hasOwnProperty.call(COUNTER_BY_ID, s);

// ---------------------------------------------------------------- pricing

/** List price at a counter, before standing. At least 1 Wick — nothing on a shelf is free. */
export function listPrice(itemId: string, markup: number): number {
  const def = ITEMS[itemId];
  if (!def) return 0;
  return Math.max(1, Math.round(def.value * markup));
}

/** What a counter charges this player for one unit: list price less their standing discount. */
export function buyPrice(itemId: string, counter: CounterDef, factionTier: number): number {
  const list = listPrice(itemId, counter.markup);
  return list === 0 ? 0 : discounted(list, factionTier);
}

/**
 * What a counter pays for a unit, as a fraction of `ItemDef.value`. The owner's number is 0.6;
 * it climbs with rarity because every counter on the Band competes for the good stock and none
 * of them competes for hull scrap. Standing does not move this — a counter's generosity is
 * about the item, its discount is about you.
 */
export const SELL_FRACTION = [0.6, 0.62, 0.65, 0.68, 0.72] as const;

export const sellFraction = (rarity: RarityLevel | number): number =>
  SELL_FRACTION[Math.max(0, Math.min(SELL_FRACTION.length - 1, Math.floor(rarity)))] ?? 0.6;

/** Wick paid for `qty` of `itemId`. Rounded once, on the total, so ×10 is not ×10 roundings. */
export function sellPrice(itemId: string, qty = 1): number {
  const def = ITEMS[itemId];
  if (!def || qty <= 0) return 0;
  return Math.max(1, Math.round(def.value * qty * sellFraction(def.rarity)));
}

/**
 * The Compact's warrant on one stack: `INSURANCE_RATE` of its value, less standing. Rounded up,
 * because the Compact does not round in your favour, but never below 1.
 */
export const INSURANCE_PREMIUM_RATE = 0.15;

export function insurancePremium(itemId: string, qty: number, factionTier: number): number {
  const def = ITEMS[itemId];
  if (!def || qty <= 0) return 0;
  const gross = Math.ceil(def.value * qty * INSURANCE_PREMIUM_RATE);
  return Math.max(1, Math.round(gross * (1 - standingDiscount(factionTier))));
}

/** Compact rep earned by selling: one point per 100 Wick taken across the counter. */
export const SELL_REP_PER_WICK = 100;
export const sellRep = (wickPaid: number): number =>
  Math.max(0, Math.floor(wickPaid / SELL_REP_PER_WICK));

// ---------------------------------------------------------------- rotation

export interface StockLine {
  itemId: string;
  /** Price before standing, so a UI can strike the sign price through. */
  list: number;
  /** True for a line that is only here today. */
  rotating: boolean;
}

/**
 * A counter's shelf on a given day. Staples first, then `rotatingCount` lines walked out of the
 * rotating pool from a day-seeded start — the same trick `contractsForDay` uses, with a stride
 * of 1 from a seeded offset so a day can never draw the same line twice.
 */
export function stockForDay(counter: CounterDef, dayIndex: number): StockLine[] {
  const lines: StockLine[] = counter.staples.map((itemId) => ({
    itemId,
    list: listPrice(itemId, counter.markup),
    rotating: false,
  }));
  const pool = counter.rotating;
  if (pool.length > 0) {
    const span = Math.min(counter.rotatingCount, pool.length);
    const start = (((Math.trunc(dayIndex) * 5) % pool.length) + pool.length) % pool.length;
    for (let k = 0; k < span; k++) {
      const itemId = pool[(start + k) % pool.length];
      if (itemId === undefined) continue;
      lines.push({ itemId, list: listPrice(itemId, counter.markup), rotating: true });
    }
  }
  return lines;
}

/** Every counter's shelf for a day, keyed by faction. */
export function marketForDay(dayIndex: number): Record<FactionId, StockLine[]> {
  const out = {} as Record<FactionId, StockLine[]>;
  for (const counter of COUNTERS) out[counter.id] = stockForDay(counter, dayIndex);
  return out;
}

/** Sanity, mirroring `validateRecipes`: every shelf id must be an item that exists. */
export function validateStock(): string[] {
  const bad: string[] = [];
  for (const counter of COUNTERS) {
    for (const id of [...counter.staples, ...counter.rotating]) {
      if (!ITEMS[id]) bad.push(`${counter.id}:${id}`);
    }
  }
  return bad;
}

/** Human label for a shelf line, for the receipt copy. */
export const stockLabel = (itemId: string): string => {
  const def = ITEMS[itemId];
  return def ? `${def.name} (${RARITY_NAMES[def.rarity] ?? 'Common'})` : itemId;
};
